import { useMemo, useState } from 'react'
import { Alert, Card, Checkbox, Col, DatePicker, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { BookOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  ACCOUNT_TYPES,
  EXPENSE_CLASSES,
  downloadFinanceExport,
  fetchTrialBalance,
  type TrialBalanceRow,
} from '@/features/finance/financeApi'
import { ReportFilterBar } from '@/features/finance/ReportFilterBar'
import { ReportTableEmpty } from '@/features/finance/ReportTableEmpty'
import { matchesSearch, matchesValues, useLocalListState } from '@/shared/lib/localList'
import { defaultDayRange } from '@/shared/lib/dayRange'
import { formatNumber } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'
import { ReportScopeNoteBar } from '@/features/finance/ReportScopeNoteBar'
import { CostCenterFilter } from '@/features/finance/CostCenterFilter'

const { RangePicker } = DatePicker
const ISO_DAY = 'YYYY-MM-DD'

/** «Faqat harakat bo'lganlar» filtri — `lineCount > 0` */
const ACTIVITY_NONZERO = 'NONZERO'

/**
 * Aylanma-qoldiq vedomosti — `GET /api/finance/reports/trial-balance`.
 *
 * <p>Mijozning «tuzilgan buxgalteriya shakli, keyinchalik ERP ga
 * uzatiladigan» talabi. Har ERP (1C, SAP, Odoo, QuickBooks) aynan shu
 * shaklni import qiladi: hisob kodi, debit aylanmasi, kredit aylanmasi.
 *
 * <h2>⚠️ `created desc` QO'LLANMAYDI — VA BU ATAYLAB</h2>
 * <p>Ikki sabab, ikkalasi ham qat'iy:
 * <ol>
 *   <li>`TrialBalanceRow` da sana maydoni UMUMAN YO'Q (`accountId`,
 *       `accountCode`, `accountName`, `accountType`, `expenseClass`,
 *       `debitMinor`, `creditMinor`, `netMinor`, `lineCount`) — ya'ni
 *       "oxirgi qo'shilgan" degan tushuncha bu javobda mavjud emas;</li>
 *   <li>vedomost HISOBLAR REJASI tartibida o'qiladi. Buxgalter uni
 *       yuqoridan pastga, kod bo'yicha tekshiradi va ERP ga ham shu
 *       tartibda uzatadi. Tartibni almashtirish hisobotni o'qib
 *       bo'lmaydigan qilardi.</li>
 * </ol>
 * <p>Shuning uchun qatorlar SERVER bergan tartibda qoladi va panel
 * ularni qayta saralamaydi.
 *
 * <h2>⚠️ SAHIFALASH YO'Q — ENDPOINT UNI QO'LLAMAYDI</h2>
 * <p>`FinanceReportController#trialBalance` faqat `from`/`to` ni oladi.
 * `page`/`size` yo'q, ya'ni sahifalash tugmalari faqat bezak bo'lardi.
 * Ro'yxat uzunligi hisoblar rejasining o'lchami bilan chegaralangan
 * (o'nlab qator), server esa kerak bo'lsa `truncated` bilan ogohlantiradi.
 *
 * <h2>⚠️ YIG'INDI QATORLARDAN QO'SHILMAYDI</h2>
 * <p>`totalDebitMinor`/`totalCreditMinor` SERVERDAN keladi va u
 * jadvaldagi filtrga BOG'LIQ EMAS. Qatorlardan yig'ilgan son
 * `truncated` holatida haqiqiydan kam chiqardi, filtrlangan holatda esa
 * butunlay boshqa savolga javob berardi. Filtr faol bo'lganda buni
 * ekranning o'zi aytadi (`totalsNote`).
 *
 * <h2>⚠️ `balanced` NI DOIRA BILAN BIRGA O'QING</h2>
 * <p>Bu sahifadagi eng oson yanglishtiradigan joy
 * ({@code TrialBalance#balanced} javadoc'i):
 * <ul>
 *   <li>to'liq doirada `false` — <b>MA'LUMOT BUZILGAN</b>: har
 *       provodkaning yig'indisi nolga teng
 *       (`finance.assert_entry_balanced()`), ya'ni ularning yig'indisi
 *       ham nol bo'lishi SHART;</li>
 *   <li>tor doirada `false` — <b>ODATIY</b>: provodkaning ikkinchi
 *       satri (bank hisobi) xarajat markaziga bog'lanmagan va tor
 *       doiraga tushmaydi.</li>
 * </ul>
 */
export function FinanceTrialBalancePage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const navigate = useNavigate()
  const filter = useLocalListState()

  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [
    dayjs(defaultDayRange(0)[1]).startOf('month'),
    defaultDayRange(0)[1],
  ])
  const [nodeId, setNodeId] = useState<string | null>(null)

  const from = range[0].format(ISO_DAY)
  const to = range[1].format(ISO_DAY)

  const report = useQuery({
    queryKey: queryKeys.finance.trialBalance(from, to, nodeId),
    queryFn: ({ signal }) => fetchTrialBalance({ from, to, nodeId }, signal),
  })

  const data = report.data
  const currency = data?.note.currency ?? ''
  const restricted = data?.note.scopeRestricted ?? false
  const partial = restricted || nodeId !== null

  const typeFilter = filter.values['type']
  const classFilter = filter.values['expenseClass']
  const onlyActive = filter.values['activity']?.[0] === ACTIVITY_NONZERO

  const rows = useMemo(() => data?.rows ?? [], [data])
  const visible = useMemo(
    () =>
      // ⚠️ QAYTA SARALASH YO'Q — server tartibi saqlanadi (sinf
      // javadoc'idagi sabab). Faqat filtrlash.
      rows.filter(
        (row) =>
          matchesSearch(
            filter.search,
            row.accountCode,
            row.accountName,
            seededName('accounts', row.accountCode, row.accountName, t),
          ) &&
          matchesValues(typeFilter, row.accountType) &&
          matchesValues(classFilter, row.expenseClass) &&
          (!onlyActive || row.lineCount > 0),
      ),
    [rows, filter.search, typeFilter, classFilter, onlyActive, t],
  )

  const columns: ColumnsType<TrialBalanceRow> = [
    {
      key: 'account',
      title: t('finance.trialBalance.columns.account'),
      render: (_, row) => (
        <Space size={6} wrap>
          <Typography.Text code>{row.accountCode}</Typography.Text>
          <Typography.Text>{seededName('accounts', row.accountCode, row.accountName, t)}</Typography.Text>
        </Space>
      ),
    },
    {
      key: 'type',
      title: t('finance.trialBalance.columns.type'),
      width: 220,
      render: (_, row) => (
        <Space size={4} wrap>
          {/* ⚠️ Tur ERKIN SATR: tarjimasi topilmasa kodning O'ZI
              chiziladi — sahifa notanish qiymatdan o'chib qolmasin.
              Kalitlar `FinanceAccountsPage` bilan AYNI (`accountType.*`),
              bir tushuncha ikki joyda ikki xil nomlanmasin. */}
          <Tag>{t(`accountType.${row.accountType}`, { defaultValue: row.accountType })}</Tag>
          {/* ⚠️ Sinf FAQAT `EXPENSE` da keladi; boshqa turda maydonning
              O'ZI javobda yo'q. Bu YETISHMAYOTGAN ma'lumot emas — savol
              o'sha turda qo'yilmaydi, shuning uchun teg ham chizilmaydi. */}
          {row.expenseClass ? (
            <Tag color="orange">
              {t(`expenseClass.${row.expenseClass}`, { defaultValue: row.expenseClass })}
            </Tag>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'debit',
      align: 'right',
      width: 170,
      title: t('finance.trialBalance.columns.debit'),
      // ⚠️ `0` — O'LCHANGAN NOL, bo'sh katak emas: server har qatorda
      // `debitMinor` ni SON sifatida beradi (`z.number()`, ixtiyoriy
      // emas). Ya'ni "0,00" bu yerda haqiqiy javob va uni chiziqcha
      // bilan almashtirish "ma'lumot yo'q" degan boshqa xabar bo'lardi.
      render: (_, row) => formatMoneyValue(money(row.debitMinor, currency)),
    },
    {
      key: 'credit',
      align: 'right',
      width: 170,
      title: t('finance.trialBalance.columns.credit'),
      render: (_, row) => formatMoneyValue(money(row.creditMinor, currency)),
    },
    {
      key: 'net',
      align: 'right',
      width: 170,
      title: t('finance.trialBalance.columns.net'),
      render: (_, row) => (
        <Typography.Text type={row.netMinor < 0 ? 'danger' : undefined}>
          {formatMoneyValue(money(row.netMinor, currency))}
        </Typography.Text>
      ),
    },
    {
      key: 'lineCount',
      align: 'right',
      width: 130,
      title: t('finance.trialBalance.columns.lineCount'),
      render: (_, row) => formatNumber(row.lineCount),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'openInChart',
              label: t('finance.trialBalance.openInChart'),
              // ⚠️ Hisoblar rejasi qidiruvni URL'dan o'qiydi (`q`),
              // ya'ni havola operatorni AYNAN shu hisobga olib boradi.
              onClick: () =>
                navigate(`/finance/accounts?q=${encodeURIComponent(row.accountCode)}`),
              permission: 'finance.account.view',
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('finance.trialBalance.title')}
        description={t('finance.trialBalance.intro')}
        extra={
          can('report.export') ? (
            <ServerExportButton
              size="middle"
              title={t('finance.trialBalance.title')}
              disabled={report.isError}
              estimatedRows={data?.rows.length ?? null}
              run={(format, onProgress) =>
                downloadFinanceExport('trial-balance', format, { from, to, nodeId, onProgress })
              }
            />
          ) : null
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Typography.Text type="secondary">{t('finance.trialBalance.rangeLabel')}:</Typography.Text>
          <RangePicker
            value={range}
            onChange={(value) => {
              if (value?.[0] && value[1]) setRange([value[0], value[1]])
            }}
            allowClear={false}
            format="DD.MM.YYYY"
          />
          <CostCenterFilter value={nodeId} onChange={setNodeId} />
        </Space>
        <ReportScopeNoteBar note={data?.note} />
      </Card>

      {restricted ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('finance.trialBalance.scopeRestricted')}
        />
      ) : null}

      {data?.truncated ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('finance.trialBalance.truncated')}
        />
      ) : null}

      {report.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(report.error, t)}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={8}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.trialBalance.kpi.debit')}
                  value={formatMoneyValue(money(data?.totalDebitMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.trialBalance.kpi.credit')}
                  value={formatMoneyValue(money(data?.totalCreditMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.trialBalance.kpi.balanced')}
                  valueStyle={data && !data.balanced && !partial ? { color: '#cf1322' } : undefined}
                  value={
                    data
                      ? data.balanced
                        ? t('finance.trialBalance.balancedYes')
                        : t('finance.trialBalance.balancedNo')
                      : '—'
                  }
                />
              </Card>
            </Col>
          </Row>

          {/*
            ⚠️ IKKI BOSHQA XABAR — sabab sahifa javadoc'ida. Tor doirada
            nomutanosiblik ODATIY, to'liq doirada esa BUZILGAN ma'lumot.
          */}
          {data && !data.balanced ? (
            <Alert
              type={partial ? 'info' : 'error'}
              showIcon
              style={{ marginBottom: 16 }}
              message={
                partial
                  ? t('finance.trialBalance.unbalancedScoped')
                  : t('finance.trialBalance.unbalancedFull')
              }
              description={
                nodeId !== null
                  ? t('finance.trialBalance.unbalancedNodeHint')
                  : restricted
                    ? t('finance.trialBalance.unbalancedScopedHint')
                    : t('finance.trialBalance.unbalancedFullHint')
              }
            />
          ) : null}

          <Card title={t('finance.trialBalance.tableTitle')} size="small">
            <ReportFilterBar
              filter={filter}
              searchPlaceholder={t('finance.trialBalance.searchPlaceholder')}
              shown={visible.length}
              loaded={rows.length}
              totalsNote={t('finance.reportTable.totalsUnfiltered')}
              extra={
                <>
                  <Select
                    allowClear
                    value={typeFilter?.[0] ?? undefined}
                    onChange={(value?: string) => filter.setValues('type', value ? [value] : [])}
                    placeholder={t('finance.trialBalance.columns.type')}
                    style={{ width: 170 }}
                    options={ACCOUNT_TYPES.map((value) => ({
                      value,
                      label: t(`accountType.${value}`, { defaultValue: value }),
                    }))}
                  />
                  <Select
                    allowClear
                    value={classFilter?.[0] ?? undefined}
                    onChange={(value?: string) =>
                      filter.setValues('expenseClass', value ? [value] : [])
                    }
                    placeholder={t('finance.trialBalance.classFilter')}
                    style={{ width: 190 }}
                    options={EXPENSE_CLASSES.map((value) => ({
                      value,
                      label: t(`expenseClass.${value}`, { defaultValue: value }),
                    }))}
                  />
                  {/* ⚠️ MA'NOLI FILTR: vedomostda harakatsiz hisoblar
                      «0,00» qatorlar bo'lib turadi va ular tekshiruvda
                      ko'zni chalg'itadi. Bu filtr ularni YASHIRADI,
                      NOLGA AYLANTIRMAYDI — yakun o'zgarmaydi. */}
                  <Checkbox
                    checked={onlyActive}
                    onChange={(e) =>
                      filter.setValues('activity', e.target.checked ? [ACTIVITY_NONZERO] : [])
                    }
                  >
                    {t('finance.trialBalance.onlyActive')}
                  </Checkbox>
                </>
              }
            />

            <Table<TrialBalanceRow>
              rowKey="accountId"
              columns={columns}
              dataSource={visible}
              loading={report.isPending}
              pagination={false}
              size="small"
              scroll={{ x: 1100 }}
              locale={{
                emptyText: (
                  <ReportTableEmpty
                    filter={filter}
                    icon={<BookOutlined />}
                    title={t('finance.trialBalance.empty')}
                    description={t('finance.trialBalance.emptyHint')}
                  />
                ),
              }}
            />
          </Card>
        </>
      )}
    </>
  )
}
